'use client';

import React, { useEffect, useRef } from 'react';
import QualifyForm from './QualifyForm';

export default function HeroSection() {
  const leftRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = leftRef.current?.querySelectorAll('[data-animate]');
    els?.forEach((el, i) => {
      (el as HTMLElement).style.animationDelay = `${i * 150}ms`;
      el.classList.add('animate-enter');
    });
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col lg:flex-row overflow-hidden pt-16">
      {/* ── LEFT HALF ── */}
      <div
        className="relative flex-1 lg:w-1/2 grid-atmosphere noise-overlay flex flex-col justify-center px-8 lg:px-16 xl:px-20 py-20 lg:py-0 overflow-hidden"
        ref={leftRef}
      >
        <div className="absolute inset-0 bg-glow-sky pointer-events-none" />
        <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-sky/6 rounded-full blur-[80px] pointer-events-none" />
        <div className="hidden lg:block absolute right-0 top-16 bottom-16 w-px bg-gradient-to-b from-transparent via-slate-mist to-transparent" />

        <div className="relative z-10 max-w-xl">
          {/* Eyebrow */}
          <div
            data-animate
            className="opacity-0 inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-sky-light border border-sky/30 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse" />
            <span className="text-xs font-semibold text-sky-dark font-body tracking-wide uppercase">
              Business · Personal · Healthcare Loans
            </span>
          </div>

          {/* The big number */}
          <div data-animate className="opacity-0 mb-2">
            <span className="font-heading font-black text-hero text-slate leading-none tracking-tight block stat-number select-none">
              48hr
            </span>
          </div>

          {/* Primary line */}
          <p
            data-animate
            className="opacity-0 font-heading font-bold text-2xl lg:text-3xl text-slate leading-snug tracking-tight mb-3"
          >
            Average time from application<br className="hidden sm:block" /> to funding decision.
          </p>

          {/* Resolution line */}
          <p
            data-animate
            className="opacity-0 font-body text-xl lg:text-2xl text-sky font-medium mb-10"
          >
            Fast approvals. Responsible lending.
          </p>

          {/* Supporting proof points */}
          <div data-animate className="opacity-0 space-y-3">
            {[
              'Loan amounts from $5,000 to $5,000,000',
              'Business, personal & healthcare loan programs',
              'Soft credit check — no impact to your score',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-sage-light flex items-center justify-center shrink-0">
                  <svg className="w-3.5 h-3.5 text-sage-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-slate-light font-body">{item}</span>
              </div>
            ))}
          </div>

          {/* Trust seal */}
          <div
            data-animate
            className="opacity-0 mt-10 flex items-center gap-3 p-3 rounded-2xl bg-white/70 border border-slate-mist/40 w-fit"
          >
            <div className="w-9 h-9 rounded-xl bg-sky-light flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-sky" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate font-body leading-tight">Responsible Lending Standards</p>
              <p className="text-xs text-slate-fog font-body">Transparent rates · No hidden fees</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT HALF: Multi-Step Form ── */}
      <div id="qualify-form-section" className="flex-1 lg:w-1/2 bg-white flex items-center justify-center px-6 lg:px-12 xl:px-16 py-16 lg:py-12 relative">
        <div className="absolute inset-0 bg-glow-sky opacity-40 pointer-events-none" />
        <div className="relative z-10 w-full max-w-lg">
          <QualifyForm />
        </div>
      </div>
    </section>
  );
}